/* eslint-disable */
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  SparklesIcon, 
  XMarkIcon, 
  PaperAirplaneIcon,
  ChatBubbleLeftRightIcon,
  PhotoIcon,
  ExclamationCircleIcon,
  DocumentTextIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  BanknotesIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  BellAlertIcon,
  PlusCircleIcon,
  MicrophoneIcon
} from '@heroicons/react/24/solid';
import { useAuthBusiness } from '../contexts/AuthBusinessContext';
import { useNewRestaurantSettings } from '../hooks/useNewRestaurantSettings';

const isLocal = window.location.hostname === 'localhost' || 
                 window.location.hostname === '127.0.0.1';
const API_BASE_URL = isLocal ? 'http://localhost:5005' : ''; // Canlıda relative path (/api) kullanması için boş bırakıyoruz (Hosting rewrite varsa)

// 🎨 AI Yanıtlarını Zenginleştiren Hafif Markdown Biçimlendirici
const renderFormattedText = (text) => {
  if (!text) return null;
  const lines = text.split('\n');

  return (
    <div className="space-y-1.5 leading-relaxed text-xs sm:text-sm">
      {lines.map((line, idx) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={idx} className="h-1" />;

        // Başlıklar: ### veya ##
        if (trimmed.startsWith('### ')) {
          return (
            <h4 key={idx} className="font-black text-violet-950 text-xs sm:text-sm pt-1 border-b border-violet-100 pb-0.5">
              {trimmed.replace(/^###\s+/, '')}
            </h4>
          );
        }
        if (trimmed.startsWith('## ')) {
          return (
            <h3 key={idx} className="font-black text-violet-900 text-sm sm:text-base pt-1.5 border-b border-violet-200 pb-1">
              {trimmed.replace(/^##\s+/, '')}
            </h3>
          );
        }

        // Madde imleri (- veya * veya 1.)
        const isBullet = trimmed.startsWith('- ') || trimmed.startsWith('* ');
        const content = isBullet ? trimmed.replace(/^[-*]\s+/, '') : trimmed;

        // **kalın** ve *italik* formatlama
        const parts = [];
        const regex = /\*\*(.*?)\*\*|\*(.*?)\*/g;
        let lastIndex = 0;
        let match;

        while ((match = regex.exec(content)) !== null) {
          if (match.index > lastIndex) {
            parts.push(content.substring(lastIndex, match.index));
          }
          if (match[1]) {
            // **bold**
            parts.push(<strong key={match.index} className="font-extrabold text-slate-900">{match[1]}</strong>);
          } else if (match[2]) {
            // *italic*
            parts.push(<em key={match.index} className="italic text-slate-600">{match[2]}</em>);
          }
          lastIndex = regex.lastIndex;
        }
        if (lastIndex < content.length) {
          parts.push(content.substring(lastIndex));
        }

        if (isBullet) {
          return (
            <div key={idx} className="flex items-start gap-1.5 pl-1 text-slate-700">
              <span className="text-violet-500 font-black mt-0.5">•</span>
              <span className="flex-1">{parts.length > 0 ? parts : content}</span>
            </div>
          );
        }

        return (
          <p key={idx} className="text-slate-700">
            {parts.length > 0 ? parts : content}
          </p>
        );
      })}
    </div>
  );
};

const AIAssistantWidget = ({ isEmbedded = false, isOpen: controlledIsOpen, onClose: controlledOnClose }) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const handleClose = () => {
    if (controlledOnClose) controlledOnClose();
    else setInternalIsOpen(false);
  };
  const [message, setMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const { selectedBusiness, currentUserRole } = useAuthBusiness();
  const [aiConfig, setAiConfig] = useState(null);

  const [isMaximized, setIsMaximized] = useState(false);
  const [analyzedExpense, setAnalyzedExpense] = useState(null);
  const [approvedActions, setApprovedActions] = useState([]);
  const [isSubmittingApproval, setIsSubmittingApproval] = useState(false);
  const { settings: restaurantSettings } = useNewRestaurantSettings();
  const [expensePaymentMethods, setExpensePaymentMethods] = useState([]);

  const [currentChatId, setCurrentChatId] = useState(Date.now());
  const [chatThreads, setChatThreads] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [dueReminders, setDueReminders] = useState([]);
  const [showReminders, setShowReminders] = useState(false);
  const [showAddReminderModal, setShowAddReminderModal] = useState(false);
  const [newReminderData, setNewReminderData] = useState({
    title: '',
    date: new Date().toISOString().split('T')[0],
    time: '18:00',
    repeat: 'once'
  });
  const [isSubmittingReminder, setIsSubmittingReminder] = useState(false);
  const [showPaymentSelector, setShowPaymentSelector] = useState(false);
  const [selectedExpensePayment, setSelectedExpensePayment] = useState(null);
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const inputRef = useRef(null);
  // Kullanıcı seçimini AI yanıtından bağımsız olarak saklar
  const forcedPaymentRef = useRef(null);

  // Web Speech API ile ücretsiz sesli yazdırma
  const toggleListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert('Tarayıcınız sesli yazdırmayı desteklemiyor. Chrome veya Edge kullanabilirsiniz.');
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.lang = 'tr-TR';
      recognition.continuous = false;
      recognition.interimResults = false;

      recognition.onstart = () => setIsListening(true);
      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        if (transcript) {
          setMessage(prev => (prev ? prev + ' ' + transcript : transcript));
        }
      };
      recognition.onerror = (event) => {
        console.warn('Ses tanıma hatası:', event.error);
        setIsListening(false);
      };
      recognition.onend = () => setIsListening(false);

      recognitionRef.current = recognition;
      recognition.start();
    } catch (err) {
      console.error('Mikrofon başlatılamadı:', err);
      setIsListening(false);
    }
  };

  // Hatırlatıcıları çek (widget açılınca)
  const loadReminders = async () => {
    if (!selectedBusiness?.id) return;
    try {
      // 1. Önce doğrudan Firebase SDK'dan anında oku (En hızlı ve güvenilir)
      const { db } = await import('../services/firebase');
      const { collection, getDocs, query, orderBy } = await import('firebase/firestore');
      const remRef = collection(db, 'businesses', 'food_beverage', 'businesses', selectedBusiness.id, 'ai_reminders');
      const q = query(remRef, orderBy('date', 'asc'));
      const snap = await getDocs(q);
      const list = snap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(r => r.status === 'pending' || r.status === 'overdue' || !r.status);
      setDueReminders(list);
    } catch(err) {
      console.warn('Firebase direct reminder read failed, trying API fallback', err);
      // 2. API Fallback
      try {
        const res = await fetch(`${API_BASE_URL}/api/ai-assistant/reminders/${selectedBusiness.id}/due`);
        const data = await res.json();
        if (data.success) setDueReminders(data.reminders || []);
      } catch(e) {}
    }
  };

  useEffect(() => {
    if (selectedBusiness?.id && isOpen) {
      loadReminders();
    }
  }, [selectedBusiness, isOpen]);

  // Tedarikçileri çek
  useEffect(() => {
    if (selectedBusiness?.id && isOpen) {
      const fetchSuppliers = async () => {
        try {
          const { db } = await import('../services/firebase');
          const { collection, getDocs, query, orderBy } = await import('firebase/firestore');
          const suppliersRef = collection(db, 'businesses', 'food_beverage', 'businesses', selectedBusiness.id, 'suppliers');
          const q = query(suppliersRef, orderBy('name', 'asc'));
          const snap = await getDocs(q);
          setSuppliers(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (err) {
          console.warn('Error fetching suppliers for AI widget:', err);
        }
      };
      fetchSuppliers();
    }
  }, [selectedBusiness, isOpen]);

  const [messages, setMessages] = useState([
    {
      id: 1,
      sender: 'ai',
      text: 'Merhaba! Ben işletmenizin dijital yönetim asistanıyım. Size raporlar, gider girişleri veya güncel satış durumu hakkında nasıl yardımcı olabilirim?',
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  ]);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (isOpen) {
      const stored = localStorage.getItem('prmanager_ai_chats');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          setChatThreads(parsed);
        } catch (err) {
          console.error(err);
        }
      }
    }
  }, [isOpen]);

  // Ödeme yöntemlerini ayarlar değiştikçe yükle
  useEffect(() => {
    const finSettings = restaurantSettings?.financial_settings;
    if (finSettings) {
      if (finSettings.expensePaymentMethods && finSettings.expensePaymentMethods.length > 0) {
        const activeMethods = finSettings.expensePaymentMethods.filter(m => m.enabled);
        setExpensePaymentMethods(activeMethods);
      } else {
        // Varsayılan yöntemleri dinamik olarak oluştur (PaymentSettingsModal ile aynı mantık)
        const defaultMethods = [
          { id: 'unpaid', name: 'Açık Hesap', enabled: true }
        ];

        if (finSettings.cashRegisters && finSettings.cashRegisters.length > 0) {
          finSettings.cashRegisters.forEach(r => {
            defaultMethods.push({ id: `cash_${r.id}`, name: `Nakit - ${r.name}`, enabled: true });
          });
        } else {
          defaultMethods.push({ id: 'cash', name: 'Nakit - Ana Kasa', enabled: true });
        }

        defaultMethods.push({ id: 'company_card', name: 'Şirket Kartı', enabled: true });
        defaultMethods.push({ id: 'bank_transfer', name: 'Banka Hesabı', enabled: true });

        setExpensePaymentMethods(defaultMethods);
      }
    }
  }, [restaurantSettings]);

  const [isVisible, setIsVisible] = useState(true);

  // Visibility Check Logic
  useEffect(() => {
    const checkVisibility = async () => {
      try {
        const { db } = await import('../services/firebase');
        const { getDoc, doc } = await import('firebase/firestore');
        
        const validRoles = ['super_admin', 'system_admin', 'owner', 'business_owner', 'manager', 'admin', 'operator', 'staff'];
        const isUserPermitted = !currentUserRole || validRoles.includes(currentUserRole);

        const settingsDoc = await getDoc(doc(db, 'admin_settings', 'global_settings'));
        if (settingsDoc.exists()) {
          const settings = settingsDoc.data().aiSettings;
          setAiConfig(settings);
          
          if (settings) {
            const isEnabled = settings.enabled !== false;
            setIsVisible(isEnabled && isUserPermitted);
          } else {
            setIsVisible(isUserPermitted);
          }
        } else {
          setIsVisible(isUserPermitted);
        }
      } catch (err) {
        setIsVisible(true);
      }
    };

    checkVisibility();
  }, [currentUserRole]);

  useEffect(() => {
    if (messages.length > 1) {
      const stored = localStorage.getItem('prmanager_ai_chats');
      let threads = [];
      if (stored) {
        try {
          threads = JSON.parse(stored);
        } catch (e) {}
      }
      
      const existingIdx = threads.findIndex(t => t.id === currentChatId);
      const firstUserMsg = messages.find(m => m.sender === 'user')?.text || 'Yeni Sohbet';
      const shortTitle = firstUserMsg.length > 30 ? firstUserMsg.slice(0, 30) + '...' : firstUserMsg;

      if (existingIdx >= 0) {
        threads[existingIdx].messages = messages;
      } else {
        threads.unshift({
          id: currentChatId,
          title: shortTitle,
          messages: messages,
          time: new Date().toLocaleDateString()
        });
      }
      localStorage.setItem('prmanager_ai_chats', JSON.stringify(threads));
      setChatThreads(threads);
    }
  }, [messages, currentChatId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, analyzedExpense]);

  const [pendingFile, setPendingFile] = useState(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [supplierCategoryMap, setSupplierCategoryMap] = useState({});
  const [activeReport, setActiveReport] = useState(null); // 📊 Aktif rapor verisi

  // Tedarikçi-kategori öğrenme haritasını çek (Feature 8)
  useEffect(() => {
    if (selectedBusiness?.id && isOpen) {
      const fetchCategoryMap = async () => {
        try {
          const { db } = await import('../services/firebase');
          const { doc, getDoc } = await import('firebase/firestore');
          const memRef = doc(db, 'businesses', 'food_beverage', 'businesses', selectedBusiness.id, 'settings', 'ai_memory');
          const memDoc = await getDoc(memRef);
          if (memDoc.exists() && memDoc.data().supplierCategoryMap) {
            setSupplierCategoryMap(memDoc.data().supplierCategoryMap);
          }
        } catch(e) {}
      };
      fetchCategoryMap();
    }
  }, [selectedBusiness, isOpen]);

  // Günlük Özet Al (Feature 2: Otomatik Rapor Yorumu)
  const handleDailySummary = async () => {
    if (!selectedBusiness?.id || isSummaryLoading) return;
    setIsSummaryLoading(true);
    setIsTyping(true);

    const requestMsg = {
      id: Date.now(),
      sender: 'user',
      text: '📊 Bugünün işletme özetini göster',
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    setMessages(prev => [...prev, requestMsg]);

    try {
      const response = await fetch(`${API_BASE_URL}/api/ai-assistant/daily-summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ businessId: selectedBusiness.id })
      });
      const data = await response.json();
      
      const aiMsg = {
        id: Date.now() + 1,
        sender: 'ai',
        text: data.success ? data.summary : `Özet alınamadı: ${data.error}`,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isSummary: true
      };
      setMessages(prev => [...prev, aiMsg]);
    } catch(e) {
      setMessages(prev => [...prev, { id: Date.now()+1, sender: 'ai', text: 'Özet alınırken hata oluştu.', time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }]);
    } finally {
      setIsTyping(false);
      setIsSummaryLoading(false);
    }
  };

  // Yetenekleri Göster (Feature Discovery)
  const handleShowCapabilities = () => {
    const capsMsg = {
      id: Date.now(),
      sender: 'ai',
      text: `🤖 **Sizin için neler yapabilirim?**\n\nİşletmenizi daha akıllı yönetmeniz için şu konularda size yardımcı olabilirim:\n\n` +
            `📊 **Raporlama:** "Bugün ne kadar satış yaptım?", "Bu haftaki cirom nedir?"\n` +
            `💸 **Gider Analizi:** "Su faturası ekle 500 TL", "Tedarikçi ödemelerini listele"\n` +
            `📸 **Fatura Okuma:** Kamera simgesine basıp fatura resmi yükleyerek otomatik kayıt oluşturma\n` +
            `📈 **Tahminleme:** "Gelecek hafta satışlar nasıl olur?" (7 Günlük projeksiyon)\n` +
            `⏰ **Hatırlatıcılar:** "Yarın sabah 9'da et siparişi vermemi hatırlat"\n` +
            `⚙️ **Menü Yönetimi:** "Lahmacun fiyatını 150 TL yap"\n\n` +
            `*Hızlı butonları kullanarak hemen başlayabilirsiniz!*`,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      isCapabilities: true
    };
    setMessages(prev => [...prev, capsMsg]);
  };

  // Body class to hide WhatsApp when AI Assistant is open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('ai-assistant-open');
    } else {
      document.body.classList.remove('ai-assistant-open');
    }
    return () => document.body.classList.remove('ai-assistant-open');
  }, [isOpen]);

  if (!isVisible) return null;

  // Satış Tahmini Handler (Feature 9)
  const handleForecast = async () => {
    if (!selectedBusiness?.id || isTyping) return;
    setIsTyping(true);
    setMessages(prev => [...prev, { id: Date.now(), sender: 'user', text: '📈 Gelecek 7 günün satış tahminini göster', time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }]);
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai-assistant/forecast`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ businessId: selectedBusiness.id })
      });
      const data = await response.json();
      if (data.success) {
        const tableText = data.forecast.map(f => `${f.dayName}: ~₺${f.predictedSales.toLocaleString('tr-TR')}`).join('\n');
        setMessages(prev => [...prev, { id: Date.now()+1, sender: 'ai', text: `**7 Günlük Satış Tahmini** ${data.trendText}\n\n${tableText}\n\n---\n${data.interpretation}`, time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }), isForecast: true }]);
      } else {
        setMessages(prev => [...prev, { id: Date.now()+1, sender: 'ai', text: `Tahmin alınamadı: ${data.error}`, time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }]);
      }
    } catch(e) {
      setMessages(prev => [...prev, { id: Date.now()+1, sender: 'ai', text: 'Tahmin alınırken hata oluştu.', time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }]);
    } finally { setIsTyping(false); }
  };

  // Document scanning logic - Updated to only set pending file
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setPendingFile({
        file,
        base64: reader.result,
        preview: URL.createObjectURL(file)
      });
    };
    reader.readAsDataURL(file);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() && !pendingFile) return;

    let finalMessage = message;
    let displayMessage = message;

    if (selectedExpensePayment) {
        // Kullanıcının seçimini ref'e kaydet (AI yanıtından bağımsız)
        const targetMethod = expensePaymentMethods.find(m => m.name === selectedExpensePayment);
        const targetId = targetMethod ? targetMethod.id : null;
        forcedPaymentRef.current = targetId || selectedExpensePayment;
        
        finalMessage = `Gider Ekle: ${message}`;
        displayMessage = `💸 **[${selectedExpensePayment}]** ${message}`;
        setSelectedExpensePayment(null);
    } else {
        forcedPaymentRef.current = null;
    }

    const userMessage = {
      id: Date.now(),
      sender: 'user',
      text: pendingFile 
        ? `📸 Belge yüklendi: ${pendingFile.file.name}${displayMessage ? `\n📝 Not: ${displayMessage}` : ''}`
        : displayMessage,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages((prev) => [...prev, userMessage]);
    setMessage('');
    setIsTyping(true);

    const currentFile = pendingFile;
    if (pendingFile) {
        setPendingFile(null);
    }

    // AI'dan dönen ödeme yöntemini listeyle eşleştiren yardımcı fonksiyon
    const mapPaymentMethod = (aiMethod) => {
      const defaultMethod = expensePaymentMethods.find(m => m.isDefault)?.id || (expensePaymentMethods.length > 0 ? expensePaymentMethods[0].id : 'unpaid');
      if (!aiMethod) return defaultMethod;
      
      // 1. Önce kesin ID veya Tam İsim eşleşmesi ara
      const exactMatch = expensePaymentMethods.find(m => 
        m.id === aiMethod || 
        m.name.toLowerCase() === aiMethod.toLowerCase()
      );
      if (exactMatch) return exactMatch.id;
      
      // 2. Kısmi isim eşleşmesi ara (Nakit - Kredi Kartı POS vs)
      const partialMatch = expensePaymentMethods.find(m => 
        m.name.toLowerCase().includes(aiMethod.toLowerCase()) ||
        aiMethod.toLowerCase().includes(m.name.toLowerCase())
      );
      
      return partialMatch ? partialMatch.id : defaultMethod;
    };

    try {
      let data;
      if (currentFile) {
        // Dosya ve metni birlikte gönder (Analyze Document)
        const response = await fetch(`${API_BASE_URL}/api/ai-assistant/analyze-document`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileData: currentFile.base64,
            mimeType: currentFile.file.type,
            businessId: selectedBusiness?.id || null,
            userDescription: finalMessage // Notu da gönderiyoruz
          })
        });
        data = await response.json();
        
        if (data.success) {
          const d = new Date(); const today = d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
          // Kullanıcı seçimi varsa zorla uygula, yoksa AI yanıtını eşleştir
          const resolvedPayment = forcedPaymentRef.current || mapPaymentMethod(data.data.paymentMethod);
          forcedPaymentRef.current = null;
          
          setAnalyzedExpense({
            draftId: data.draftId,
            documentDate: today, // Her zaman bugün
            paymentMethod: resolvedPayment,
            ...data.data,
            // Spread sonrası zorla üstlerine yaz
            documentDate: today,
            paymentMethod: resolvedPayment,
          });
          // Onaylanacak varsayılan aksiyonları set et
          const initialActions = [
            ...(data.data.proposedActions || []),
            ...(data.data.items?.filter(i => i.isNewProduct).map(i => ({ type: 'create_stock_item', name: i.name, category: i.category, groupId: i.groupId })) || [])
          ];
          setApprovedActions(initialActions);
          
          const aiMsg = {
            id: Date.now() + 1,
            sender: 'ai',
            text: '🎉 Belge başarıyla okundu! Lütfen aşağıdaki detayları kontrol edip onaylayın:',
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          };
          setMessages((prev) => [...prev, aiMsg]);
        } else {
          throw new Error(data.error || 'Yapay zeka belgeyi okuyamadı.');
        }
      } else {
        // Sadece metin gönder (Chat)
        const response = await fetch(`${API_BASE_URL}/api/ai-assistant/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            message: userMessage.text,
            businessId: selectedBusiness?.id || null,
            history: messages.map(m => ({ sender: m.sender, text: m.text }))
          })
        });
        data = await response.json();
        
        if (!response.ok || !data.success) {
          throw new Error(data.error || 'Sunucu hatası oluştu');
        }
        
        // Eğer backend bir aksiyon (İşletme Ekleme vb.) önerdiyse onay formunu çıkar
        if (data.action === 'add_expense' && data.data) {
          const d = new Date(); const today = d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
          // Kullanıcı seçimi varsa zorla uygula
          const resolvedPayment = forcedPaymentRef.current || mapPaymentMethod(data.data.paymentMethod);
          forcedPaymentRef.current = null;
          
          const detectedType = data.data.type || (data.data.category === 'isletme_giderleri' || data.data.category === 'satin_alma_giderleri' ? data.data.category : 'gunluk_giderler');
          setAnalyzedExpense({
            ...data.data,
            totalAmount: data.data.amount,
            category: data.data.category || 'Diğer',
            type: detectedType,
            documentDate: today, // Her zaman bugün
            paymentMethod: resolvedPayment,
          });
        }

        let aiResponseText = data.response || '';
        let actionData = null;

        // 🛡️ Genişletilmiş JSON Ayıklama: backtick, double-quote ve single-quote JSON
        const jsonBlockMatch = aiResponseText.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
        const nakedDoubleQuote = aiResponseText.match(/(\{[\s\S]*?"action"[\s\S]*?\})/);
        const nakedSingleQuote = aiResponseText.match(/(\{[\s\S]*?'action'[\s\S]*?\})/);

        const finalMatch = jsonBlockMatch || nakedDoubleQuote || nakedSingleQuote;

        if (finalMatch) {
          try {
            let rawJson = (finalMatch[1] || finalMatch[0]);
            // eslint-disable-next-line no-control-regex
            rawJson = rawJson.replace(/[\u0000-\u001F\u007F-\u009F]/g, '');
            // Single-quote → double-quote dönüşümü
            rawJson = rawJson.replace(/'/g, '"');
            // Eksik parantez tamiri
            const openBraces = (rawJson.match(/{/g) || []).length;
            const closeBraces = (rawJson.match(/}/g) || []).length;
            const openBrackets = (rawJson.match(/\[/g) || []).length;
            const closeBrackets = (rawJson.match(/]/g) || []).length;
            if (openBrackets > closeBrackets) rawJson += ']'.repeat(openBrackets - closeBrackets);
            if (openBraces > closeBraces) rawJson += '}'.repeat(openBraces - closeBraces);

            const parsed = JSON.parse(rawJson);
            if (parsed && parsed.action) {
              actionData = parsed;
              // Eşleşen bloğu (backtickler dahil) metinden sil
              aiResponseText = aiResponseText.replace(finalMatch[0], '').trim();
            }
          } catch (e) {
            console.error('🛡️ JSON Parse Error (Handled):', e, 'Raw JSON:', finalMatch[1] || finalMatch[0]);
          }
        }

        // add_expense aksiyonu için sadece tutar varsa ve kullanıcı açıkça gider eklemek istiyorsa formu aç
        const isReminderIntent = /hatırlat|maç|randevu|alarm|not et/i.test(userMessage.text);
        if (actionData?.action === 'add_expense' && parseFloat(actionData.amount) > 0 && !isReminderIntent) {
          const d = new Date(); const today = d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
          const resolvedPayment = forcedPaymentRef.current || mapPaymentMethod(actionData.paymentMethod);
          forcedPaymentRef.current = null;
          const detectedType = actionData.type || (actionData.category === 'isletme_giderleri' || actionData.category === 'satin_alma_giderleri' ? actionData.category : 'gunluk_giderler');
          setAnalyzedExpense({
            ...actionData,
            totalAmount: actionData.amount,
            category: actionData.category || 'Diğer',
            type: detectedType,
            documentDate: today,
            paymentMethod: resolvedPayment,
          });
        } else if (actionData?.action === 'set_reminder' || data.action === 'set_reminder') {
          // ⏰ Hatırlatıcı kuruldu - Listeyi anında güncelle
          if (selectedBusiness?.id) {
            try {
              const res = await fetch(`${API_BASE_URL}/api/ai-assistant/reminders/${selectedBusiness.id}/due`);
              const remData = await res.json();
              if (remData.success) setDueReminders(remData.reminders || []);
            } catch(e) {}
          }
          const cleanText = aiResponseText.trim() || `⏰ Hatırlatıcı başarıyla oluşturuldu!`;
          const aiMessage = {
            id: Date.now() + 1,
            sender: 'ai',
            text: cleanText,
            action: actionData || data,
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          };
          setMessages((prev) => [...prev, aiMessage]);
        } else {
          const cleanText = aiResponseText.trim();
          if (cleanText) {
            const aiMessage = {
              id: Date.now() + 1,
              sender: 'ai',
              text: cleanText,
              action: actionData,
              time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
            };
            setMessages((prev) => [...prev, aiMessage]);
          }
        }
      }
      
      setIsTyping(false);

    } catch (error) {
      console.error('AI Request Error:', error);
      const aiMessage = {
        id: Date.now() + 1,
        sender: 'ai',
        text: `Hata oluştu: ${error.message || 'Lütfen tekrar deneyin.'}`,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setMessages((prev) => [...prev, aiMessage]);
      setIsTyping(false);
    }
  };

  const handleApproveExpense = async () => {
    if (!analyzedExpense || (!analyzedExpense.id && !analyzedExpense.draftId)) return;
    setIsSubmittingApproval(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/ai-assistant/approve-expense`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          draftId: analyzedExpense.id || analyzedExpense.draftId,
          businessId: selectedBusiness?.id || null,
          type: analyzedExpense.type || analyzedExpense.category || 'gunluk_giderler',
          amount: parseFloat(analyzedExpense.totalAmount) || 0,
          description: analyzedExpense.description || '',
          supplierName: analyzedExpense.supplierName || '',
          documentDate: analyzedExpense.documentDate || (() => { const d = new Date(); return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0'); })(),
          documentNo: analyzedExpense.documentNo || '',
          items: analyzedExpense.items || [],
          approvedActions: approvedActions // Yeni alan: Kullanıcı onayları
        })
      });

      const data = await response.json();
      setIsSubmittingApproval(false);
      
      if (data.success) {
        setAnalyzedExpense(null);
        setMessages(prev => [...prev, {
          id: Date.now() + 2,
          sender: 'ai',
          text: '✓ Harcama kaydı onaylandı ve ön muhasebe sistemine işlendi. Cari (Tedarikçi) kontrolü yapıldı.',
          time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }]);
      } else {
        alert(data.error || 'Onaylama sırasında bir hata oluştu.');
      }
    } catch (err) {
      setIsSubmittingApproval(false);
      alert('Onaylama sırasında hata: ' + err.message);
    }
  };

  // Widget görünürlüğü artık DashboardLayout üzerinden isMainHubPage ile merkezi yönetiliyor.

  return (
    <>
      {/* Floating Button kaldırıldı: Artık DashboardLayout tarafından tek elden yönetiliyor */}

      {/* Chat Panel */}
      {isOpen && createPortal(
        <div className={isMaximized 
          ? "fixed inset-0 z-[100000] flex flex-col bg-white opacity-100 w-full h-full overflow-hidden animate-fade-in"
          : "fixed bottom-3 right-3 sm:bottom-6 sm:right-6 z-[100000] w-[calc(100vw-24px)] max-w-[390px] h-[calc(100vh-24px)] max-h-[600px] flex flex-col bg-white opacity-100 rounded-[2rem] shadow-2xl border border-slate-200 overflow-hidden animate-slide-up origin-bottom-right"}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 bg-white border-b border-slate-100 shrink-0">
            <div className="flex items-center space-x-3">
              <div className="relative w-10 h-10 rounded-full bg-gradient-to-tr from-violet-600 to-fuchsia-600 flex items-center justify-center shadow-md">
                <SparklesIcon className="w-5 h-5 text-white" />
                <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></div>
              </div>
              <div>
                <h3 className="font-black text-sm text-slate-800 tracking-tight">İşletme Asistanı</h3>
                <p className="text-[10px] text-slate-400 font-bold tracking-widest uppercase mt-0.5">Yapay Zeka Hazır</p>
              </div>
            </div>
            <div className="flex items-center space-x-1">
              <button 
                onClick={() => setShowHistory(!showHistory)}
                className="p-2 text-slate-400 hover:text-violet-600 hover:bg-violet-50 rounded-full transition-colors flex items-center justify-center"
                title="Geçmiş Sohbetler"
              >
                <DocumentTextIcon className="w-5 h-5" />
              </button>
              <button 
                onClick={() => setIsMaximized(!isMaximized)}
                className="p-2 text-slate-400 hover:text-violet-600 hover:bg-violet-50 rounded-full transition-colors hidden sm:flex items-center justify-center"
                title={isMaximized ? "Küçült" : "Tam Ekran Yap"}
              >
                {isMaximized 
                  ? <ArrowsPointingInIcon className="w-5 h-5" />
                  : <ArrowsPointingOutIcon className="w-5 h-5" />}
              </button>
              <button 
                onClick={handleClose}
                className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* History View */}
          {showHistory && (
            <div className="flex-1 flex flex-col min-h-0 bg-slate-50 overflow-y-auto p-4 space-y-3">
              <div className="flex items-center justify-between shrink-0">
                <h4 className="text-xs font-black text-slate-700">Geçmiş Sohbetler</h4>
                <button 
                  onClick={() => {
                    setCurrentChatId(Date.now());
                    setMessages([{
                      id: 1,
                      sender: 'ai',
                      text: 'Yeni bir sohbet başlattınız. Size nasıl yardımcı olabilirim?',
                      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    }]);
                    setShowHistory(false);
                  }}
                  className="px-3 py-1 bg-violet-600 text-white rounded-full text-[10px] font-black hover:bg-violet-700 transition-colors shadow-md select-none"
                >
                  + Yeni Sohbet
                </button>
              </div>
              <div className="flex-1 overflow-y-auto space-y-2">
                {chatThreads.map(thread => (
                  <div 
                    key={thread.id}
                    className="p-3 bg-white hover:bg-violet-50/50 border border-slate-100 rounded-xl cursor-pointer transition-colors flex items-center justify-between"
                    onClick={() => {
                      setCurrentChatId(thread.id);
                      setMessages(thread.messages);
                      setShowHistory(false);
                    }}
                  >
                    <div className="flex flex-col min-w-0">
                      <span className="text-xs font-bold text-slate-700 truncate">{thread.title}</span>
                      <span className="text-[9px] text-slate-400 font-medium mt-0.5">{thread.time}</span>
                    </div>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        const updated = chatThreads.filter(t => t.id !== thread.id);
                        localStorage.setItem('prmanager_ai_chats', JSON.stringify(updated));
                        setChatThreads(updated);
                        if (currentChatId === thread.id) {
                          setCurrentChatId(Date.now());
                          setMessages([{
                            id: 1,
                            sender: 'ai',
                            text: 'Merhaba! Ben işletmenizin dijital yönetim asistanıyım.',
                            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                          }]);
                        }
                      }}
                      className="p-1.5 hover:text-red-500 text-slate-300 transition-colors select-none rounded-full hover:bg-red-50"
                      title="Sohbeti Sil"
                    >
                      <XMarkIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                {chatThreads.length === 0 && (
                  <div className="text-center text-xs font-medium text-slate-400 py-6">Kayıtlı sohbet bulunamadı.</div>
                )}
              </div>
            </div>
          )}

          {!showHistory && (
            <>
              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/50 scrollbar-hide">

                {/* Hatırlatıcı Paneli */}
                {showReminders && (
                  <div className="bg-white rounded-2xl border border-violet-100 shadow-md overflow-hidden mb-3 animate-slide-up">
                    <div className="flex items-center justify-between px-3.5 py-2.5 bg-gradient-to-r from-violet-50 to-fuchsia-50 border-b border-slate-100">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs">⏰</span>
                        <span className="text-[11px] font-black text-violet-900 uppercase tracking-wider">İşletme Hatırlatıcıları</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => setShowAddReminderModal(!showAddReminderModal)}
                          className="text-[10px] font-black px-2 py-0.5 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors shadow-2xs"
                        >
                          {showAddReminderModal ? 'Kapat' : '+ Yeni Ekle'}
                        </button>
                        <button onClick={() => setShowReminders(false)} className="text-slate-400 hover:text-slate-600 text-xs px-1">✕</button>
                      </div>
                    </div>

                    {/* Manuel Hatırlatıcı Ekleme Formu */}
                    {showAddReminderModal && (
                      <div className="p-3 bg-violet-50/50 border-b border-violet-100 space-y-2">
                        <input
                          type="text"
                          placeholder="Hatırlatma Başlığı (Örn: Maç var, Su faturası, Sebze alımı)"
                          value={newReminderData.title}
                          onChange={(e) => setNewReminderData({ ...newReminderData, title: e.target.value })}
                          className="w-full text-xs font-medium px-3 py-2 bg-white border border-violet-200 rounded-xl outline-none focus:ring-2 focus:ring-violet-500/20"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-[9px] font-black uppercase text-slate-400 mb-0.5">Tarih</label>
                            <input
                              type="date"
                              value={newReminderData.date}
                              onChange={(e) => setNewReminderData({ ...newReminderData, date: e.target.value })}
                              className="w-full text-xs font-semibold px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-[9px] font-black uppercase text-slate-400 mb-0.5">Saat</label>
                            <input
                              type="time"
                              value={newReminderData.time}
                              onChange={(e) => setNewReminderData({ ...newReminderData, time: e.target.value })}
                              className="w-full text-xs font-semibold px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg outline-none"
                            />
                          </div>
                        </div>
                        <div className="flex items-center justify-between gap-2 pt-1">
                          <select
                            value={newReminderData.repeat}
                            onChange={(e) => setNewReminderData({ ...newReminderData, repeat: e.target.value })}
                            className="text-[11px] font-semibold px-2 py-1.5 bg-white border border-slate-200 rounded-lg outline-none"
                          >
                            <option value="once">Tek Seferlik</option>
                            <option value="daily">Her Gün</option>
                            <option value="weekly">Haftalık</option>
                            <option value="monthly">Aylık</option>
                          </select>
                          <button
                            type="button"
                            disabled={!newReminderData.title.trim() || isSubmittingReminder}
                            onClick={async () => {
                              if (!selectedBusiness?.id || !newReminderData.title.trim()) return;
                              setIsSubmittingReminder(true);
                              try {
                                const newReminderItem = {
                                  title: newReminderData.title.trim(),
                                  date: newReminderData.date || new Date().toISOString().split('T')[0],
                                  time: newReminderData.time || '18:00',
                                  repeat: newReminderData.repeat || 'once',
                                  status: 'pending',
                                  createdAt: new Date(),
                                  createdBy: 'user_direct'
                                };

                                // 1. Doğrudan Firestore'a yaz (Anında ve kesin kayıt)
                                try {
                                  const { db } = await import('../services/firebase');
                                  const { collection, addDoc } = await import('firebase/firestore');
                                  const remRef = collection(db, 'businesses', 'food_beverage', 'businesses', selectedBusiness.id, 'ai_reminders');
                                  const docRef = await addDoc(remRef, newReminderItem);
                                  newReminderItem.id = docRef.id;
                                } catch (fsErr) {
                                  console.warn('Direct Firestore save failed, fallback to API:', fsErr);
                                  // API Fallback
                                  const response = await fetch(`${API_BASE_URL}/api/ai-assistant/reminders/${selectedBusiness.id}`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify(newReminderItem)
                                  });
                                  const resData = await response.json();
                                  if (resData.reminder) newReminderItem.id = resData.reminder.id;
                                }

                                // 2. State'i anında güncelle
                                setDueReminders(prev => [...prev, newReminderItem].sort((a,b) => (a.date || '').localeCompare(b.date || '')));
                                setShowAddReminderModal(false);
                                setNewReminderData({
                                  title: '',
                                  date: new Date().toISOString().split('T')[0],
                                  time: '18:00',
                                  repeat: 'once'
                                });
                              } catch(e) {
                                console.error('Reminder save error:', e);
                                alert('Hatırlatıcı kaydedilirken bir hata oluştu: ' + (e.message || ''));
                              } finally {
                                setIsSubmittingReminder(false);
                              }
                            }}
                            className="flex-1 text-xs font-bold py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors disabled:opacity-50 text-center"
                          >
                            {isSubmittingReminder ? 'Kaydediliyor...' : '✓ Kaydet'}
                          </button>
                        </div>
                      </div>
                    )}

                    {dueReminders.length === 0 ? (
                      <p className="text-[11px] text-slate-400 text-center py-4">Kayıtlı aktif hatırlatıcı yok. Yukarıdan ekleyebilir veya chatten yazabilirsiniz.</p>
                    ) : (
                      <div className="divide-y divide-slate-50 max-h-48 overflow-y-auto">
                        {dueReminders.map(r => (
                          <div key={r.id} className="flex items-center justify-between px-3.5 py-2 hover:bg-slate-50 group transition-colors">
                            <div className="flex-1 min-w-0">
                              <p className="text-[12px] font-bold text-slate-800 truncate">{r.title}</p>
                              <p className="text-[10px] text-slate-400">
                                {r.date === (() => { const d = new Date(); return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0'); })() ? '🔴 Bugün' : '🟡 ' + r.date} 
                                {r.time ? ` • ${r.time}` : ''} 
                                {r.repeat !== 'once' ? ` • ${r.repeat === 'weekly' ? 'Haftalık' : r.repeat === 'monthly' ? 'Aylık' : 'Günlük'}` : ''}
                              </p>
                            </div>
                            <div className="flex gap-1 ml-2 opacity-90 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={async () => {
                                  setDueReminders(prev => prev.filter(x => x.id !== r.id));
                                  try {
                                    const { db } = await import('../services/firebase');
                                    const { doc, updateDoc } = await import('firebase/firestore');
                                    await updateDoc(doc(db, 'businesses', 'food_beverage', 'businesses', selectedBusiness.id, 'ai_reminders', r.id), {
                                      status: 'done',
                                      doneAt: new Date()
                                    });
                                  } catch (err) {
                                    await fetch(`${API_BASE_URL}/api/ai-assistant/reminders/${selectedBusiness?.id}/${r.id}/done`, { method: 'POST' });
                                  }
                                }}
                                className="text-[10px] px-2 py-1 bg-emerald-100 text-emerald-700 rounded-md font-bold hover:bg-emerald-200"
                              >✓ Tamam</button>
                              <button
                                onClick={async () => {
                                  setDueReminders(prev => prev.filter(x => x.id !== r.id));
                                  try {
                                    const { db } = await import('../services/firebase');
                                    const { doc, deleteDoc } = await import('firebase/firestore');
                                    await deleteDoc(doc(db, 'businesses', 'food_beverage', 'businesses', selectedBusiness.id, 'ai_reminders', r.id));
                                  } catch (err) {
                                    await fetch(`${API_BASE_URL}/api/ai-assistant/reminders/${selectedBusiness?.id}/${r.id}`, { method: 'DELETE' });
                                  }
                                }}
                                className="text-[10px] px-2 py-1 bg-rose-100 text-rose-700 rounded-md font-bold hover:bg-rose-200"
                              >✕</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="text-center mb-6">
              <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-3 py-1 rounded-full uppercase tracking-widest">
                Bugün
              </span>
            </div>

            {messages.map((msg) => (
              <div key={msg.id} className={`flex flex-col ${msg.sender === 'user' ? 'items-end' : 'items-start'}`}>
                <div className="flex items-end space-x-2 max-w-[85%]">
                  {msg.sender === 'ai' && (
                    <div className="w-6 h-6 rounded-full bg-gradient-to-tr from-violet-500 to-fuchsia-500 flex items-center justify-center flex-shrink-0 mb-1">
                      <SparklesIcon className="w-3 h-3 text-white" />
                    </div>
                  )}
                  <div 
                    className={`p-3 rounded-2xl text-sm ${
                      msg.sender === 'user' 
                        ? 'bg-violet-600 text-white rounded-br-none shadow-md' 
                        : 'bg-white text-slate-700 rounded-bl-none shadow-sm border border-slate-100'
                    }`}
                  >
                    <div className="whitespace-pre-wrap">
                      {msg.sender === 'ai' ? renderFormattedText(msg.text) : msg.text}
                    </div>
                    
                    {/* 📊 Rapor Aksiyon Butonu */}
                    {msg.action?.action === 'show_report' && (
                      <button
                        onClick={() => setActiveReport(msg.action)}
                        className="mt-3 flex items-center justify-center gap-2 w-full py-2.5 bg-violet-50 text-violet-700 rounded-xl text-xs font-black hover:bg-violet-100 transition-all border border-violet-100 active:scale-95"
                      >
                        📊 {msg.action.title || 'Detaylı Raporu Görüntüle'}
                      </button>
                    )}
                  </div>
                </div>
                <span className="text-[9px] text-slate-400 font-medium mt-1 px-8">
                  {msg.time}
                </span>
              </div>
            ))}

            {isTyping && (
              <div className="flex items-start space-x-2">
                <div className="w-6 h-6 rounded-full bg-gradient-to-tr from-violet-500 to-fuchsia-500 flex items-center justify-center flex-shrink-0">
                  <SparklesIcon className="w-3 h-3 text-white animate-pulse" />
                </div>
                <div className="bg-white p-3 rounded-2xl rounded-bl-none shadow-sm border border-slate-100 flex space-x-1">
                  <div className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce"></div>
                  <div className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '0.15s' }}></div>
                  <div className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '0.3s' }}></div>
                </div>
              </div>
            )}

            {/* Kompakt Gider Onay Kartı */}
            {analyzedExpense && (
              <div className="bg-white p-3.5 m-1 rounded-2xl border border-violet-100 shadow-lg space-y-2.5 backdrop-blur-md animate-slide-up">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs">🧾</span>
                    <span className="text-xs font-black text-slate-800 tracking-tight">Yeni Gider Algılandı</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={`text-[9px] font-black px-2 py-0.5 rounded-md ${
                      analyzedExpense.type === 'satin_alma_giderleri' 
                        ? 'bg-amber-50 text-amber-700 border border-amber-200' 
                        : analyzedExpense.type === 'isletme_giderleri' 
                        ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                        : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                    }`}>
                      {analyzedExpense.type === 'satin_alma_giderleri' 
                        ? 'Satın Alma' 
                        : analyzedExpense.type === 'isletme_giderleri' 
                        ? 'İşletme Gideri' 
                        : 'Günlük Gider'}
                    </span>
                    <span className="text-[9px] font-black px-1.5 py-0.5 bg-violet-50 text-violet-600 rounded-md">Taslak</span>
                  </div>
                </div>

                {analyzedExpense.isDuplicate && (
                  <div className="bg-rose-50 border border-rose-100 p-2 rounded-xl flex items-center gap-1.5 animate-pulse">
                    <ExclamationCircleIcon className="w-4 h-4 text-rose-500 shrink-0" />
                    <span className="text-[10px] font-bold text-rose-700">Bu fiş daha önce kaydedilmiş olabilir!</span>
                  </div>
                )}

                <div className="bg-slate-50 p-2.5 rounded-xl space-y-1.5 border border-slate-100 text-xs">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 font-bold text-[10px] uppercase">Tedarikçi / Başlık</span>
                    <span className="font-extrabold text-slate-800 truncate max-w-[170px]">{analyzedExpense.supplierName || 'Belirtilmedi'}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 font-bold text-[10px] uppercase">Toplam Tutar</span>
                    <span className="font-black text-emerald-600 text-sm">₺{parseFloat(analyzedExpense.totalAmount || analyzedExpense.amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400 font-bold text-[10px] uppercase">Ödeme Türü</span>
                    <span className="font-bold text-slate-700 text-[11px]">
                      {expensePaymentMethods.find(m => m.id === analyzedExpense.paymentMethod)?.name || analyzedExpense.paymentMethod || 'Açık Hesap'}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2 pt-1">
                  <button 
                    type="button"
                    onClick={handleApproveExpense}
                    disabled={isSubmittingApproval}
                    className="flex-1 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-extrabold text-xs py-2.5 rounded-xl hover:brightness-110 shadow-md shadow-violet-500/20 active:scale-95 transition-all text-center disabled:opacity-50"
                  >
                    {isSubmittingApproval ? 'İşleniyor...' : '✓ Hemen Onayla'}
                  </button>
                  <button 
                    type="button"
                    onClick={() => setIsExpenseModalOpen(true)}
                    disabled={isSubmittingApproval}
                    className="px-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs py-2.5 rounded-xl active:scale-95 transition-all"
                    title="Detayları Düzenle"
                  >
                    ✏️ Düzenle
                  </button>
                  <button 
                    type="button"
                    onClick={() => setAnalyzedExpense(null)}
                    disabled={isSubmittingApproval}
                    className="px-2.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-colors text-xs"
                    title="İptal Et"
                  >
                    ✕
                  </button>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-4 bg-white border-t border-slate-100 shrink-0 relative">
            {/* Gider Ekle Ödeme Seçim Menüsü (Input alanı üstünde, overflow'dan etkilenmeyen popup) */}
            {showPaymentSelector && (
              <div className="absolute bottom-full left-4 mb-2 w-52 bg-white border border-rose-200 rounded-2xl shadow-2xl overflow-hidden animate-slide-up z-50">
                <div className="bg-rose-50 px-3 py-2 border-b border-rose-100 flex items-center justify-between">
                  <span className="text-[10px] font-black text-rose-700 uppercase tracking-widest">Ödeme Yöntemi Seçin</span>
                  <button type="button" onClick={() => setShowPaymentSelector(false)} className="text-rose-400 hover:text-rose-700 text-xs">✕</button>
                </div>
                <div className="max-h-48 overflow-y-auto divide-y divide-slate-50">
                  {expensePaymentMethods.map(method => (
                    <button
                      key={method.id}
                      type="button"
                      onClick={() => {
                        setSelectedExpensePayment(method.name);
                        setShowPaymentSelector(false);
                        setTimeout(() => inputRef.current?.focus(), 100);
                      }}
                      className="w-full text-left px-3.5 py-2.5 text-[11px] font-bold text-slate-700 hover:bg-rose-50 hover:text-rose-700 transition-colors"
                    >
                      {method.name}
                    </button>
                  ))}
                  {expensePaymentMethods.length === 0 && (
                    <div className="px-3 py-2 text-[10px] font-bold text-slate-400">Yöntem bulunamadı.</div>
                  )}
                </div>
              </div>
            )}

            {pendingFile && (
              <div className="mb-3 flex items-center p-2 bg-violet-50 rounded-2xl border border-violet-100 animate-slide-up">
                <div className="relative">
                  <img 
                    src={pendingFile.preview} 
                    alt="preview" 
                    className="w-12 h-12 rounded-lg object-cover border border-violet-200"
                  />
                  <button 
                    onClick={() => setPendingFile(null)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 shadow-md hover:bg-red-600 transition-colors"
                  >
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </div>
                <div className="ml-3 overflow-hidden">
                  <p className="text-[10px] font-black text-violet-600 truncate">{pendingFile.file.name}</p>
                  <p className="text-[9px] text-violet-400 font-bold uppercase tracking-tight">Gönderilmeye hazır...</p>
                </div>
              </div>
            )}
            <form onSubmit={handleSendMessage} className="relative flex flex-col">
              {/* Hızlı Aksiyon Çipleri (Ekrana Tam Sığacak Şekilde Dengeli ve Kompakt) */}
              {!analyzedExpense && (
                <div className="flex items-center justify-between gap-1 px-0.5 mb-2.5 w-full">
                  <button
                    type="button"
                    onClick={() => setShowPaymentSelector(!showPaymentSelector)}
                    className="flex-1 flex items-center justify-center gap-1 py-1.5 px-1.5 bg-rose-50 text-rose-700 hover:bg-rose-100 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all active:scale-95 border border-rose-200 truncate"
                  >
                    <BanknotesIcon className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">Gider</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleDailySummary}
                    disabled={isSummaryLoading || !selectedBusiness?.id}
                    className="flex-1 flex items-center justify-center gap-1 py-1.5 px-1.5 bg-violet-50 text-violet-700 hover:bg-violet-100 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all active:scale-95 disabled:opacity-50 border border-violet-200 truncate"
                  >
                    <ChartBarIcon className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{isSummaryLoading ? '...' : 'Özet'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleForecast}
                    disabled={isTyping}
                    className="flex-1 flex items-center justify-center gap-1 py-1.5 px-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all active:scale-95 border border-blue-200 disabled:opacity-50 truncate"
                  >
                    <ArrowTrendingUpIcon className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">Tahmin</span>
                  </button>

                  {/* Kompakt İkon Buton: Hatırlatıcılar */}
                  <button
                    type="button"
                    onClick={() => {
                      setShowReminders(prev => {
                        const next = !prev;
                        if (next && selectedBusiness?.id) {
                          fetch(`${API_BASE_URL}/api/ai-assistant/reminders/${selectedBusiness.id}/due`)
                            .then(res => res.json())
                            .then(d => { if (d.success) setDueReminders(d.reminders || []); })
                            .catch(err => console.warn('Reminder fetch failed', err));
                        }
                        return next;
                      });
                    }}
                    title={`Hatırlatıcılar ${dueReminders.length > 0 ? `(${dueReminders.length} Yaklaşan)` : ''}`}
                    className={`relative p-2 rounded-xl transition-all active:scale-95 border shrink-0 ${
                      dueReminders.length > 0 
                        ? 'bg-rose-50 text-rose-700 border-rose-200 animate-pulse' 
                        : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100 hover:text-slate-900'
                    }`}
                  >
                    <BellAlertIcon className="w-3.5 h-3.5" />
                    {dueReminders.length > 0 && (
                      <span className="absolute -top-1 -right-1 bg-rose-500 text-white rounded-full w-3.5 h-3.5 flex items-center justify-center text-[8px] font-black shadow-xs">
                        {dueReminders.length}
                      </span>
                    )}
                  </button>

                  {/* Kompakt İkon Buton: Yardım / Neler Yapabilirsin */}
                  <button
                    type="button"
                    onClick={handleShowCapabilities}
                    className="p-2 bg-slate-50 text-slate-600 hover:bg-slate-100 hover:text-slate-900 rounded-xl transition-all active:scale-95 border border-slate-200 shrink-0"
                    title="Yardım ve Yetenekler"
                  >
                    <SparklesIcon className="w-3.5 h-3.5 text-violet-500" />
                  </button>
                </div>
              )}

              {/* Input Satırı */}
              <div className="relative flex items-center bg-slate-100 rounded-full pl-12 pr-20 transition-all focus-within:ring-2 focus-within:ring-violet-500/20">
                <label
                  className="absolute left-1 p-2 text-slate-400 hover:text-violet-600 cursor-pointer rounded-full hover:bg-slate-200 transition-all active:scale-90 select-none"
                  title="Fatura/Belge Yükle ve Tara"
                >
                  <PhotoIcon className="w-5 h-5" />
                  <input 
                    type="file" 
                    accept="image/*,.pdf,.xml" 
                    onChange={handleFileUpload} 
                    className="hidden" 
                  />
                </label>

                <div className="flex items-center flex-1 overflow-x-auto scrollbar-hide py-2.5 min-h-[44px]">
                  {selectedExpensePayment && (
                    <div className="flex items-center gap-1 bg-violet-100 text-violet-700 px-2.5 py-1 rounded-full text-[10px] font-black tracking-tight mr-1.5 shrink-0 border border-violet-200 shadow-sm animate-fade-in max-w-[140px]">
                      <span className="truncate">💸 {selectedExpensePayment}</span>
                      <button 
                        type="button" 
                        onClick={() => setSelectedExpensePayment(null)}
                        className="bg-violet-200 hover:bg-violet-300 text-violet-800 rounded-full p-0.5 transition-colors shrink-0"
                      >
                        <XMarkIcon className="w-3 h-3" />
                      </button>
                    </div>
                  )}

                  <input 
                    ref={inputRef}
                    type="text" 
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={
                      isListening 
                        ? "Dinleniyor... Konuşun..." 
                        : (pendingFile 
                            ? "Fatura hakkında notunuz..." 
                            : (selectedExpensePayment ? "Açıklama ve tutar girin..." : "Soru sorun veya sesle komut verin..."))
                    }
                    className="flex-1 bg-transparent text-sm text-slate-700 placeholder-slate-400 outline-none min-w-[140px]"
                  />
                </div>

                {/* 🎙️ Ücretsiz Web Speech API Sesli Yazma Butonu */}
                <button
                  type="button"
                  onClick={toggleListening}
                  className={`absolute right-10 p-2 rounded-full transition-all active:scale-90 ${
                    isListening 
                      ? 'bg-rose-500 text-white animate-pulse shadow-md shadow-rose-500/30' 
                      : 'text-slate-400 hover:text-violet-600 hover:bg-slate-200'
                  }`}
                  title={isListening ? "Dinlemeyi durdur" : "Sesli komut ver (Ücretsiz Türkçe Ses Tanıma)"}
                >
                  <MicrophoneIcon className="w-4 h-4" />
                </button>

                {/* Gönder Butonu */}
                <button 
                  type="submit"
                  disabled={(!message.trim() && !pendingFile) || isTyping}
                  className="absolute right-1 p-2 bg-violet-600 text-white rounded-full hover:bg-violet-700 disabled:opacity-50 disabled:hover:bg-violet-600 transition-colors shadow-md active:scale-95 select-none"
                >
                  <PaperAirplaneIcon className="w-4 h-4 -mr-0.5" />
                </button>
              </div>
            </form>
          </div>
            </>
          )}
        </div>,
        document.body
      )}

      {/* 📊 Detaylı Rapor Modalı */}
      {activeReport && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-2xl max-h-[80vh] rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-violet-100 text-violet-600 rounded-lg">
                  <SparklesIcon className="w-5 h-5" />
                </div>
                <h3 className="font-bold text-slate-800">{activeReport.title || 'Detaylı Rapor'}</h3>
              </div>
              <button 
                onClick={() => setActiveReport(null)}
                className="p-2 hover:bg-slate-200 rounded-full transition-colors"
              >
                <XMarkIcon className="w-6 h-6 text-slate-400" />
              </button>
            </div>
            
            <div className="flex-1 overflow-auto p-6">
              {Array.isArray(activeReport.data) && activeReport.data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="text-xs text-slate-400 uppercase bg-slate-50 font-black">
                      <tr>
                        {Object.keys(activeReport.data[0]).map(key => (
                          <th key={key} className="px-4 py-3">{key.replace(/([A-Z])/g, ' $1').trim()}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {activeReport.data.map((row, i) => (
                        <tr key={i} className="hover:bg-slate-50 transition-colors">
                          {Object.values(row).map((val, j) => (
                            <td key={j} className="px-4 py-3 text-slate-600 font-medium">
                              {typeof val === 'number' ? val.toLocaleString('tr-TR') : String(val)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-400">
                  Veri bulunamadı.
                </div>
              )}
            </div>
            
            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end">
              <button 
                onClick={() => setActiveReport(null)}
                className="px-6 py-2 bg-slate-800 text-white rounded-xl text-sm font-bold hover:bg-slate-900 transition-all shadow-lg active:scale-95"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🧾 Detaylı Gider Düzenleme & Onay Modalı */}
      {isExpenseModalOpen && analyzedExpense && (
        <div className="fixed inset-0 z-[100001] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white w-full max-w-lg rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200 max-h-[90vh]">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/70">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-violet-100 text-violet-600 rounded-xl">
                  <BanknotesIcon className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-extrabold text-sm text-slate-800">Gider Bilgilerini Düzenle</h3>
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Ön Muhasebe Kayıt Kontrolü</p>
                </div>
              </div>
              <button 
                onClick={() => setIsExpenseModalOpen(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-full transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div>
                <label className="text-[10px] font-black text-slate-500 uppercase block mb-1.5">Gider Türü</label>
                <select 
                  value={analyzedExpense.type || analyzedExpense.category} 
                  onChange={(e) => setAnalyzedExpense({...analyzedExpense, type: e.target.value, category: e.target.value})}
                  className="w-full bg-slate-50 border border-slate-200 p-2.5 text-xs font-bold rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/20 transition-all"
                >
                  <option value="gunluk_giderler">Günlük Giderler</option>
                  <option value="isletme_giderleri">İşletme Giderleri</option>
                  <option value="satin_alma_giderleri">Satın Alma Giderleri</option>
                </select>
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-500 uppercase block mb-1.5">Tedarikçi / Muhatap</label>
                <input 
                  type="text" 
                  list="ai_suppliers_list_modal"
                  value={analyzedExpense.supplierName || ''} 
                  onChange={(e) => setAnalyzedExpense({...analyzedExpense, supplierName: e.target.value})}
                  placeholder="Tedarikçi veya kişi adı..."
                  className="w-full bg-slate-50 border border-slate-200 p-2.5 text-xs font-bold rounded-xl focus:ring-2 focus:ring-violet-500/20 outline-none"
                />
                <datalist id="ai_suppliers_list_modal">
                  {suppliers.map(s => (
                    <option key={s.id} value={s.name} />
                  ))}
                </datalist>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-black text-slate-500 uppercase block mb-1.5">Tutar (₺)</label>
                  <input 
                    type="number" 
                    value={analyzedExpense.totalAmount || analyzedExpense.amount || 0} 
                    onChange={(e) => setAnalyzedExpense({...analyzedExpense, totalAmount: e.target.value, amount: e.target.value})}
                    className="w-full bg-slate-50 border border-slate-200 p-2.5 text-xs font-bold rounded-xl focus:ring-2 focus:ring-violet-500/20 outline-none"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-black text-slate-500 uppercase block mb-1.5">Ödeme Yöntemi</label>
                  <select 
                    value={analyzedExpense.paymentMethod || 'unpaid'} 
                    onChange={(e) => setAnalyzedExpense({...analyzedExpense, paymentMethod: e.target.value})}
                    className="w-full bg-slate-50 border border-slate-200 p-2.5 text-xs font-bold rounded-xl focus:ring-2 focus:ring-violet-500/20 outline-none"
                  >
                    {expensePaymentMethods.map(method => (
                      <option key={method.id} value={method.id}>{method.name}</option>
                    ))}
                    {expensePaymentMethods.length === 0 && (
                      <>
                        <option value="cash">Nakit</option>
                        <option value="credit_card">Kredi Kartı</option>
                        <option value="unpaid">Açık Hesap</option>
                      </>
                    )}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-500 uppercase block mb-1.5">Belge / Fatura Tarihi</label>
                <input 
                  type="date"
                  value={analyzedExpense.documentDate || (() => { const d = new Date(); return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0'); })()}
                  onChange={(e) => setAnalyzedExpense({...analyzedExpense, documentDate: e.target.value})}
                  className="w-full bg-slate-50 border border-slate-200 p-2.5 text-xs font-bold rounded-xl focus:ring-2 focus:ring-violet-500/20 outline-none"
                />
              </div>

              <div>
                <label className="text-[10px] font-black text-slate-500 uppercase block mb-1.5">Açıklama</label>
                <textarea 
                  value={analyzedExpense.description || ''} 
                  onChange={(e) => setAnalyzedExpense({...analyzedExpense, description: e.target.value})}
                  rows="2"
                  className="w-full bg-slate-50 border border-slate-200 p-2.5 text-xs font-bold rounded-xl resize-none focus:ring-2 focus:ring-violet-500/20 outline-none"
                  placeholder="Gider açıklaması..."
                />
              </div>

              {/* Satır Kalemleri */}
              {analyzedExpense.items && analyzedExpense.items.length > 0 && (
                <div>
                  <label className="text-[10px] font-black text-slate-500 uppercase block mb-1.5">Satır Kalemleri</label>
                  <div className="max-h-36 overflow-y-auto bg-slate-50 p-2.5 rounded-xl border border-slate-200 space-y-2">
                    {analyzedExpense.items.map((item, idx) => (
                      <div key={idx} className="flex flex-col border-b border-slate-200/60 last:border-0 pb-1.5">
                        <div className="flex justify-between items-center text-xs font-bold text-slate-700">
                          <span className="truncate pr-2">{item.name} x {item.quantity} {item.taxRate ? `(%${item.taxRate})` : ''}</span>
                          <span className="shrink-0 text-slate-900 font-extrabold">₺{item.total}</span>
                        </div>
                        {item.isNewProduct && (
                          <div className="flex items-center mt-1">
                            <input 
                              type="checkbox" 
                              id={`modal_new_stock_${idx}`}
                              defaultChecked={true}
                              onChange={(e) => {
                                const action = { type: 'create_stock_item', name: item.name, category: item.category, groupId: item.groupId };
                                if (e.target.checked) setApprovedActions(prev => [...prev, action]);
                                else setApprovedActions(prev => prev.filter(a => a.name !== item.name));
                              }}
                              className="w-3.5 h-3.5 text-violet-600 rounded"
                            />
                            <label htmlFor={`modal_new_stock_${idx}`} className="ml-1.5 text-[9px] font-black text-amber-600 uppercase">Yeni Stok Kartı Oluştur</label>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex gap-3 justify-end">
              <button 
                type="button"
                onClick={() => setIsExpenseModalOpen(false)}
                className="px-4 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-xl text-xs font-bold transition-all"
              >
                Vazgeç
              </button>
              <button 
                type="button"
                onClick={async () => {
                  await handleApproveExpense();
                  setIsExpenseModalOpen(false);
                }}
                disabled={isSubmittingApproval}
                className="px-6 py-2.5 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-xl text-xs font-extrabold hover:brightness-110 transition-all shadow-lg shadow-violet-500/20 active:scale-95 disabled:opacity-50"
              >
                {isSubmittingApproval ? 'İşleniyor...' : '✓ Kaydet ve Onayla'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slide-up {
          0% { opacity: 0; transform: translateY(20px) scale(0.95); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-slide-up {
          animation: slide-up 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .animate-bounce-soft {
          animation: bounce-soft 2s infinite;
        }
        @keyframes bounce-soft {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        .scrollbar-hide::-webkit-scrollbar {
            display: none;
        }
        .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
        }
      `}</style>
    </>
  );
};

export default AIAssistantWidget;

